declare global {
  interface Window {
    TRACKER_HOSTS?: string[];
  }
}

type CacheStatus = 'network' | 'disk-or-memory' | 'sw' | 'unknown';

type ResourceEvent = {
  url: string;
  origin: string;
  initiator: string;
  duration_ms: number;
  ttfb_ms: number;
  dns_ms: number;
  tcp_ms: number;
  tls_ms: number;
  download_ms: number;
  transfer_size: number;
  encoded_size: number;
  decoded_size: number;
  next_hop: string;
  cache_status: CacheStatus;
  cf_cache: string | null;
  server_timing_raw: string;
};

const HOSTS = new Set<string>([
  globalThis.location.host,
  ...(globalThis.window.TRACKER_HOSTS ?? []),
]);

const events: ResourceEvent[] = [];

function round(n: number): number {
  return Math.round(n * 10) / 10;
}

function detectCache(r: PerformanceResourceTiming): CacheStatus {
  if (r.workerStart > 0 && r.responseStart - r.workerStart < 5) return 'sw';
  // Safari возвращает encodedBodySize === 0 для disk/memory-кэша (Chrome — реальный
  // размер). Универсальный сигнал «соединения не было» — пустой nextHopProtocol.
  if (r.transferSize === 0 && r.nextHopProtocol === '') return 'disk-or-memory';
  if (r.transferSize === 0 && r.encodedBodySize > 0) return 'disk-or-memory';
  if (r.transferSize > 0) return 'network';
  return 'unknown';
}

function pickCfCache(serverTiming: readonly PerformanceServerTiming[] | undefined): string | null {
  if (!serverTiming) return null;
  return serverTiming.find((s) => s.name === 'cf-cache')?.description ?? null;
}

function rawServerTiming(serverTiming: readonly PerformanceServerTiming[] | undefined): string {
  if (!serverTiming || serverTiming.length === 0) return '';
  return serverTiming.map((s) => `${s.name}=${s.description ?? ''}`).join('|');
}

function toEvent(r: PerformanceResourceTiming): ResourceEvent {
  const url = new URL(r.name);
  return {
    url: url.pathname,
    origin: url.host,
    initiator: r.initiatorType,
    duration_ms: round(r.responseEnd - r.startTime),
    ttfb_ms: round(r.responseStart - r.requestStart),
    dns_ms: round(r.domainLookupEnd - r.domainLookupStart),
    tcp_ms: round(r.connectEnd - r.connectStart),
    tls_ms: r.secureConnectionStart > 0 ? round(r.connectEnd - r.secureConnectionStart) : 0,
    download_ms: round(r.responseEnd - r.responseStart),
    transfer_size: r.transferSize,
    encoded_size: r.encodedBodySize,
    decoded_size: r.decodedBodySize,
    next_hop: r.nextHopProtocol,
    cache_status: detectCache(r),
    cf_cache: pickCfCache(r.serverTiming),
    server_timing_raw: rawServerTiming(r.serverTiming),
  };
}

function process(r: PerformanceResourceTiming): void {
  const host = new URL(r.name).host;
  if (!HOSTS.has(host)) return;
  const event = toEvent(r);
  events.push(event);
  // eslint-disable-next-line no-console
  console.log(
    `[cdn-tracker] ${event.cache_status.padEnd(15)} ` +
      `cf=${(event.cf_cache ?? '—').padEnd(8)} ` +
      `${event.duration_ms.toString().padStart(6)}ms ` +
      `${event.transfer_size.toString().padStart(7)}B ` +
      `${event.url}`,
  );
}

const observer = new PerformanceObserver((list) => {
  for (const entry of list.getEntries()) {
    process(entry as PerformanceResourceTiming);
  }
});

observer.observe({ type: 'resource', buffered: true });

window.addEventListener('load', () => {
  setTimeout(() => {
    // eslint-disable-next-line no-console
    console.log(`[cdn-tracker] collected ${events.length} events:`);
    // eslint-disable-next-line no-console
    console.table(events);
  }, 500);
});

export {};
